import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';

import { cn } from '../lib/utils';

const checkboxVariants = cva(
  // The box edge is derived from the density control heights (same knob
  // button.tsx rides), exposed as --checkbox-size so the indicator glyph can
  // scale in lockstep.
  //
  // WCAG 2.5.8 AA (Target Size, Minimum) — the ramp is FLOORED at 24 CSS px.
  // Radix renders the control as a real <button role="checkbox">, so the box
  // edge IS the pointer target: there is no larger parent hit area to fall
  // back on. Before the floor, `comfortable` produced sm=14px / default=16px
  // and `compact` produced 10.5px / 12px, and the 2026-07-29 touch-target
  // sweep measured five 16x16px row-select checkboxes on the alarms console
  // and five more on the production work-order list — every one of them below
  // the 24px AA minimum, on the two densest surfaces an operator uses. The
  // floor is applied to the TOKEN rather than to the class list so the
  // indicator glyph, which is derived from the same variable, scales with it.
  'peer shrink-0 rounded-sm border border-primary ring-offset-background size-[var(--checkbox-size)] [&_svg]:size-[calc(var(--checkbox-size)*0.75)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
  {
    variants: {
      size: {
        sm: '[--checkbox-size:max(calc(var(--control-height-sm)*0.4375),24px)]',
        default: '[--checkbox-size:max(calc(var(--control-height-sm)*0.5),24px)]',
        lg: '[--checkbox-size:max(calc(var(--control-height-md)*0.5),24px)]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> &
    VariantProps<typeof checkboxVariants>
>(({ className, size, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(checkboxVariants({ size }), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn('flex items-center justify-center text-current')}
    >
      <Check />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox, checkboxVariants };
